import * as Yup from 'yup';

import { Text } from '@components';
import {
  ApiEndpoint,
  columnDirection,
  FormActionType,
  FormType,
  InputType,
  originType,
  ServiceModalName,
  Translation,
} from '@enums';
import i18n from '@translations';
import type { EndpointType, FormConfig, FormConfigMapping } from '@types';
import { optionType } from '@utils';

interface GetApplicationFormConfigParams {
  endpoint?: EndpointType;
  applicationOptionList?: optionType[];
  schema: Yup.AnySchema;
}

export const getCdnApplicationFormConfig = ({
  endpoint = ApiEndpoint.DEFAULT,
  applicationOptionList = [],
  schema,
}: GetApplicationFormConfigParams): FormConfig => {
  const tCdnApplicationForm: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Form}:addApplication.content.${key}`);

  const tModal: (key: string) => string = (key: string): string => i18n.t(`${Translation.Cdn}:modals.${key}`);

  const tValidation: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Validation}:format.${key}`);

  const tCommonIp: (key: string) => string = (key: string): string => i18n.t(`${Translation.Common}:ip.${key}`);

  const tOriginForm: (key: string) => string = (key: string): string =>
    i18n.t(`${Translation.Configuration}:forms.origin.${key}`);

  const APPLICATION_FORM: FormConfigMapping = {
    [ApiEndpoint.DEFAULT]: {
      formType: FormType.Cdn,
      endpoint: ApiEndpoint.DEFAULT,
      formActionType: FormActionType.Create,
      modalName: ServiceModalName.FormModal,
      label: {
        title: tModal('addNewApplication'),
        description: tModal('addNewApplicationDescription'),
        confirmButton: tModal('addNewApplication'),
      },
      inputList: [
        {
          name: 'name',
          defaultValue: '',
          inputType: InputType.Text,
          placeholder: tCdnApplicationForm('enterYourApplicationName'),
          label: tCdnApplicationForm('applicationName'),
          direction: columnDirection.Row,
          required: true,
        },
        {
          name: 'domain',
          defaultValue: '',
          inputType: InputType.Text,
          label: tCdnApplicationForm('domainName'),
          placeholder: tCdnApplicationForm('enterYourDomain'),
          hints: tValidation('domain'),
          direction: columnDirection.Row,
          required: true,
        },
        {
          name: 'type',
          defaultValue: originType.HTTP,
          inputType: InputType.Select,
          placeholder: tCdnApplicationForm('selectYourOriginType'),
          label: tCdnApplicationForm('originType'),
          direction: columnDirection.Row,
          options: applicationOptionList,
          className: 'text-gray-400',
        },
        {
          name: 'origin',
          defaultValue: '',
          inputType: InputType.Text,
          placeholder: tCdnApplicationForm('enterYourOrigin'),
          label: (
            <Text $level={5} $customizeColor className="text-gray-700 text-left font-normal leading-6">
              {`${tCdnApplicationForm('origin')}`}
              <span className="text-gray-400 text-us pl-2"> {`(${tCommonIp('ipv4Address')})`}</span>
            </Text>
          ),
          direction: columnDirection.Row,
          hints: tValidation('ip'),
        },
        {
          name: 'verify_ssl_cert',
          defaultValue: false,
          inputType: InputType.Switch,
          label: tOriginForm('verifySSL'),
          direction: columnDirection.Row,
          mobileTextInput: 'justify-between',
          hide: {
            field: 'type',
            condition: (value: string) => value !== originType.HTTPS,
          },
        },
      ],
      validationSchema: schema,
    },
  };

  return (APPLICATION_FORM[endpoint] ?? APPLICATION_FORM[ApiEndpoint.DEFAULT]) as FormConfig;
};
